import { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { LoadingScreen } from '../ui/Spinner';
import { api } from '../../api/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, sessionValidated, validateSession, setUser } = useAuthStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const isMountedRef = useRef(true);

  // Check if coming from OAuth callback
  const isFromOAuth = searchParams.get('auth') === 'success';

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // If we have a user but session not validated, validate it
    if (user && !sessionValidated) {
      validateSession();
      return;
    }

    // If coming from OAuth and no user, try to fetch user from cookies
    if (isFromOAuth && !user && !authChecked && !isCheckingAuth) {
      setIsCheckingAuth(true);
      api.getMe()
        .then((response) => {
          if (isMountedRef.current && response.success && response.user) {
            setUser(response.user);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch user after OAuth:', error);
        })
        .finally(() => {
          if (isMountedRef.current) {
            setIsCheckingAuth(false);
            setAuthChecked(true);
          }
        });
    }
  }, [user, sessionValidated, validateSession, isFromOAuth, authChecked, isCheckingAuth, setUser]);

  // Show loading while checking auth from OAuth
  if (isFromOAuth && !authChecked && !user) {
    return <LoadingScreen message="Completing login..." />;
  }

  // No user after auth check - redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading while validating session
  if (!sessionValidated) {
    return <LoadingScreen message="Validating session..." />;
  }

  return <>{children}</>;
}

export function AdminRoute({ children }: ProtectedRouteProps) {
  const { user, sessionValidated, validateSession, setUser } = useAuthStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const isMountedRef = useRef(true);

  const isFromOAuth = searchParams.get('auth') === 'success';

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (user && !sessionValidated) {
      validateSession();
      return;
    }

    if (isFromOAuth && !user && !authChecked && !isCheckingAuth) {
      setIsCheckingAuth(true);
      api.getMe()
        .then((response) => {
          if (isMountedRef.current && response.success && response.user) {
            setUser(response.user);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch user after OAuth:', error);
        })
        .finally(() => {
          if (isMountedRef.current) {
            setIsCheckingAuth(false);
            setAuthChecked(true);
          }
        });
    }
  }, [user, sessionValidated, validateSession, isFromOAuth, authChecked, isCheckingAuth, setUser]);

  if (isFromOAuth && !authChecked && !user) {
    return <LoadingScreen message="Completing login..." />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!sessionValidated) {
    return <LoadingScreen message="Validating session..." />;
  }

  // Check if user has admin role
  const isAdmin = user.roles?.includes('admin');
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export function GuestRoute({ children }: ProtectedRouteProps) {
  const { user, sessionValidated } = useAuthStore();
  const location = useLocation();

  // If user is logged in, redirect to dashboard
  if (user && sessionValidated) {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}
